import * as XLSX from "xlsx";
import {
  PROFESSIONAL_TAX_MONTHLY,
  calculateGrossFromStructure,
  getSheetDisplayBasicSalary,
  isTraineeDesignation,
  toNumber,
} from "./salaryCalculations";

const normalizeHeader = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");

const HEADER_ALIASES = {
  employeeName: [
    "employees name",
    "employee name",
    "name",
    "employee",
  ],
  email: ["email", "employee email", "work email"],
  userId: ["user id", "userid", "employee id", "id"],
  monthlySalary: ["salary per month", "monthly salary", "salary", "gross salary"],
  basicSalary: [
    "minimum rate of salary / month (basic)",
    "minimum rate of salary / month",
    "basic salary",
    "basic",
  ],
  designation: ["designation", "role", "title"],
  leaveDays: ["leaves", "leave days", "leave"],
  leaveDeduction: ["leaves deduction", "leave deduction", "leave ded"],
  workedDays: ["total worked days", "worked days", "days worked"],
  tds: ["tds", "tax deducted"],
  professionalTax: ["professional tax", "pt"],
};

const resolveField = (row, field) => {
  const aliases = HEADER_ALIASES[field] || [field];
  for (const [key, value] of Object.entries(row)) {
    const normalized = normalizeHeader(key);
    if (aliases.includes(normalized)) {
      return value;
    }
  }
  return undefined;
};

const parseRow = (row, rowNumber) => {
  const employeeName = String(resolveField(row, "employeeName") ?? "").trim();
  const email = String(resolveField(row, "email") ?? "").trim();
  const userIdRaw = resolveField(row, "userId");
  const userId =
    userIdRaw != null && userIdRaw !== "" && Number.isFinite(Number(userIdRaw))
      ? Number(userIdRaw)
      : null;

  if (!employeeName && !email && !userId) {
    return { error: "Employee name, email, or user id is required." };
  }

  const monthlySalary = toNumber(resolveField(row, "monthlySalary"));
  if (monthlySalary < 0) {
    return { error: "Monthly salary cannot be negative." };
  }

  const ptRaw = resolveField(row, "professionalTax");
  const professionalTaxEnabled =
    ptRaw != null && ptRaw !== "" ? toNumber(ptRaw) > 0 : true;

  const workedRaw = resolveField(row, "workedDays");
  return {
    payload: {
      rowNumber,
      userId: userId || undefined,
      employeeName: employeeName || undefined,
      email: email || undefined,
      designation: String(resolveField(row, "designation") ?? "").trim() || undefined,
      monthlySalary,
      professionalTaxEnabled,
      basicSalary: toNumber(resolveField(row, "basicSalary")),
      accommodation: toNumber(resolveField(row, "accommodation")),
      leaveDays: toNumber(resolveField(row, "leaveDays")),
      leaveDeduction:
        resolveField(row, "leaveDeduction") != null && resolveField(row, "leaveDeduction") !== ""
          ? toNumber(resolveField(row, "leaveDeduction"))
          : undefined,
      workedDays: workedRaw != null && workedRaw !== "" ? toNumber(workedRaw) : "",
      ytdPaidLeavesBeforeMonth: 0,
      tds:
        resolveField(row, "tds") != null && resolveField(row, "tds") !== ""
          ? toNumber(resolveField(row, "tds"))
          : undefined,
      professionalTax:
        resolveField(row, "professionalTax") != null && resolveField(row, "professionalTax") !== ""
          ? toNumber(resolveField(row, "professionalTax"))
          : undefined,
    },
  };
};

export const MANUAL_SHEET_IMPORT_TEMPLATE_HEADERS = [
  "Employees Name",
  "Email",
  "User Id",
  "Salary Per Month",
  "Minimum Rate of Salary / Month (Basic)",
  "Designation",
  "Leaves",
  "Leaves Deduction",
  "TDS",
  "Professional Tax",
];

const CEO_ROLE_ID = 5;
const CMO_ROLE_ID = 2;

const isCeoOrCmoEmployee = (employee = {}) => {
  const roleId = Number(employee.roleId);
  if (roleId === CEO_ROLE_ID || roleId === CMO_ROLE_ID) return true;
  const designation = String(employee.designation || "").trim().toLowerCase();
  return /\b(ceo|cmo)\b/.test(designation);
};

/**
 * Build template rows from employees already in the system (for bulk historical import).
 */
export const buildManualSheetImportTemplateRows = ({
  employees = [],
  structures = [],
} = {}) => {
  const structureByUserId = new Map(
    structures.map((structure) => [Number(structure.userId), structure])
  );

  return employees.filter((employee) => !isCeoOrCmoEmployee(employee)).map((employee) => {
    const userId = Number(employee.id);
    const structure = structureByUserId.get(userId);
    const employeeName =
      `${employee.firstname || ""} ${employee.lastname || ""}`.trim() || employee.email || "";
    const designation = structure?.designation || employee.designation || "";
    const monthlySalary = structure ? calculateGrossFromStructure(structure) : "";
    const isTrainee = isTraineeDesignation(designation);
    const professionalTax = isTrainee ? 0 : PROFESSIONAL_TAX_MONTHLY;
    const displayBasic =
      monthlySalary !== "" && monthlySalary > 0
        ? getSheetDisplayBasicSalary({
            monthlySalary,
            designation,
            professionalTax,
          })
        : "";

    return {
      "Employees Name": employeeName,
      Email: employee.email || "",
      "User Id": userId,
      "Salary Per Month": monthlySalary !== "" ? monthlySalary : "",
      "Minimum Rate of Salary / Month (Basic)": displayBasic,
      Designation: designation,
      Leaves: 0,
      "Leaves Deduction": 0,
      TDS: "",
      "Professional Tax": professionalTax,
    };
  });
};

export const downloadManualSheetImportTemplate = ({
  employees = [],
  structures = [],
  sheetLabel = "",
} = {}) => {
  const rows = buildManualSheetImportTemplateRows({ employees, structures });
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(
    rows.length
      ? rows
      : [
          {
            "Employees Name": "",
            Email: "",
            "User Id": "",
            "Salary Per Month": "",
            "Minimum Rate of Salary / Month (Basic)": "",
            Designation: "",
            Leaves: 0,
            "Leaves Deduction": 0,
            TDS: "",
            "Professional Tax": PROFESSIONAL_TAX_MONTHLY,
          },
        ]
  );
  const sheetName = sheetLabel ? sheetLabel.slice(0, 31) : "Salary import";
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const fileSuffix = sheetLabel
    ? sheetLabel.replace(/\s+/g, "-").toLowerCase()
    : "template";
  XLSX.writeFile(workbook, `manual-salary-sheet-${fileSuffix}.xlsx`);
};

export const parseManualSheetImportFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) {
    throw new Error("The Excel file has no worksheets.");
  }
  const rows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
  if (!rows.length) {
    throw new Error("The Excel file has no data rows.");
  }

  const entries = [];
  const parseErrors = [];

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const line = String(resolveField(row, "employeeName") ?? "").trim()
      || String(resolveField(row, "email") ?? "").trim();
    if (!line && !resolveField(row, "userId")) return;

    const parsed = parseRow(row, rowNumber);
    if (parsed.error) {
      parseErrors.push({ rowNumber, message: parsed.error });
      return;
    }
    entries.push(parsed.payload);
  });

  if (!entries.length && parseErrors.length) {
    throw new Error(parseErrors[0].message);
  }
  if (!entries.length) {
    throw new Error("No valid employee rows found in the file.");
  }

  return { entries, parseErrors };
};
